import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import DashboardLayout from '../components/DashboardLayout';
import CreatorBundles from '../components/CreatorBundles';
import { FiShoppingBag, FiGift, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const StorePage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading || profileLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-white mb-4">
            Profile Required
          </h2>
          <p className="text-gray-400 mb-8">
            Create a profile to access the store.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-lime-500 to-green-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-glow-lime">
            <SafeIcon icon={FiShoppingBag} className="text-dark-950 text-2xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            HubID Store
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Unlock premium features, themes, and tools to enhance your Web3 identity
          </p>
        </div>

        {/* Creator Bundles */}
        <section>
          <div className="flex items-center space-x-2 mb-6">
            <SafeIcon icon={FiGift} className="text-lime-400 text-xl" />
            <h2 className="text-2xl font-bold text-white">Creator Bundles</h2>
          </div>
          <CreatorBundles profile={profile} theme={{}} />
        </section>
        
        {/* Benefits Section */}
        <section className="glass-dark rounded-xl border border-dark-700 p-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">
            Why Upgrade to Premium?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center mx-auto mb-3 border border-lime-500/50">
                <SafeIcon icon={FiStar} className="text-lime-400 text-xl" />
              </div>
              <h4 className="font-semibold text-white mb-2">Stand Out</h4>
              <p className="text-dark-300 text-sm">
                Premium themes make your profile unique and professional
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center mx-auto mb-3 border border-lime-500/50">
                <SafeIcon icon={FiGift} className="text-lime-400 text-xl" />
              </div>
              <h4 className="font-semibold text-white mb-2">Great Value</h4>
              <p className="text-dark-300 text-sm">
                Bundle deals save you up to 40% compared to individual purchases
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center mx-auto mb-3 border border-lime-500/50">
                <SafeIcon icon={FiShoppingBag} className="text-lime-400 text-xl" />
              </div>
              <h4 className="font-semibold text-white mb-2">Early Access</h4>
              <p className="text-dark-300 text-sm">
                Get early access to new features and exclusive content
              </p>
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default StorePage;